import { useState, useRef, useEffect, useCallback, Fragment, useMemo } from "react";
import { InlineIcon } from "@iconify/react/dist/iconify.js";
import _ from "lodash";
import { BackButton } from "./componets";
import { createQuotationChat } from "../../../api/quotation";
import Markdown from "react-markdown";
import { toast } from "react-toastify";
import { brand } from "../../../context";

interface Message {
  chat_session_uid: string;
  id?: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

type CopierProps = {
  content: string;
};

function Copier({ content }: CopierProps) {
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(content);
        toast.success("Copied to clipboard");
      }}
      className="ml-2 hover:opacity-80 cursor-pointer hover:bg-gray-200 rounded-md! p-1 hover:shadow-sm"
      title="Copy message"
    >
      <InlineIcon icon="mdi:content-copy" className="w-4 h-4" />
      <span className="sr-only">Copy to clipboard</span>
    </button>
  );
}

async function SSEChatClient(
  chatSessionUid: string,
  inputValue: string,
  setMessage: (message: string, chatSessionUid: string) => void,
  setSSEDone: () => void,
) {
  const response = await createQuotationChat({
    message: inputValue,
    chat_session_uid: chatSessionUid,
  });
  const reader = response.body?.getReader();
  if (!reader) {
    setSSEDone();
    return;
  }

  const decoder = new TextDecoder("utf-8");

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      setSSEDone();
      break;
    }

    const text = decoder.decode(value, { stream: true });
    const textArray = text.split("\n");
    for (const line of textArray) {
      if (line.trim() === "") continue;
      try {
        const json = JSON.parse(line);
        setMessage(json.content, json.chat_session_uid);
      } catch {
        setSSEDone();
        break;
      }
    }
  }
}

type QuotationRequestChatProps = {
  onBack: () => void;
  onGenerateQuotation: (message: string) => void;
};

export function QuotationRequestChat({ onBack, onGenerateQuotation }: QuotationRequestChatProps) {
  const [chatSessionUid, setChatSessionUid] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: "smooth" });
  }, []);

  const totalMessageContentLength = useMemo(() => {
    return messages.reduce((acc, message) => acc + (message?.content ?? "").length, 0);
  }, [messages]);

  useEffect(() => {
    if (totalMessageContentLength === 0) {
      return;
    }
    scrollToBottom();
  }, [scrollToBottom, totalMessageContentLength]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      chat_session_uid: chatSessionUid,
      id: Date.now().toString(),
      content: inputValue,
      role: "user",
      timestamp: new Date(),
    };
    const placeHolderMessage: Message = {
      chat_session_uid: chatSessionUid,
      id: Date.now().toString(),
      content: "",
      role: "assistant",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage, placeHolderMessage]);

    setIsLoading(true);
    setInputValue("");

    await SSEChatClient(
      chatSessionUid,
      inputValue,
      (message: string, chatSessionUid: string) => {
        setChatSessionUid(chatSessionUid);
        setMessages((prev) => {
          const newMessages = _.cloneDeep(prev);
          const lastMessage = _.last(newMessages);
          if (lastMessage) {
            lastMessage.content = message;
            lastMessage.chat_session_uid = chatSessionUid;
          }
          return newMessages;
        });
      },
      () => {
        setIsLoading(false);
      },
    );
  }

  return (
    <Fragment>
      <div className="Modal__card__body overflow-hidden grid pb-0!">
        <div className="grid gap-3 grid-rows-[1fr_auto] h-full overflow-hidden">
          <div
            className="overflow-y-auto p-4 space-y-4 border border-gray-200 rounded-md"
            ref={messagesRef}
          >
            {messages.map((message, idx) => (
              <div
                key={`${idx}-${message.id}`}
                className={`grid ${message.role === "user" ? "justify-items-end" : "justify-items-start"
                  } auto-rows-max`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${message.role === "user" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-800"
                    }`}
                >
                  <span className="text-xs opacity-70 mb-1 grid grid-cols-[1fr_auto]">
                    <span>{_.capitalize(message.role)}</span>
                    <Copier content={message.content} />
                  </span>
                  <p className="whitespace-normal [&_li]:list-disc [&_li]:ml-6">
                    <Markdown disallowedElements={["h1", "h2", "h3", "h4"]}>
                      {message.content}
                    </Markdown>
                  </p>
                  <span className="text-xs opacity-70 mt-1 block">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="grid justify-items-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="grid grid-flow-col gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit} className="rounded-md">
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your trip request..."
                className="p-2 border border-gray-200 rounded-4xl focus:outline-none"
                disabled={isLoading}
              />
              <button
                type="submit"
                className="rounded-full! border-gray-200 border-2! p-3 hover:bg-gray-400 hover:[&_svg]:text-white transition-all"
                onClick={handleSubmit}
              >
                <InlineIcon icon="mdi:arrow-up" className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="Modal__card__actions justify-end">
        <BackButton onBack={onBack} />
        {messages.length > 1 &&
          messages[messages.length - 1].role === "assistant" &&
          messages[messages.length - 1].content.trim() !== "" && (
            <button
              type="button"
              className="Button"
              data-brand={brand}
              onClick={() => onGenerateQuotation(messages[messages.length - 1].content)}
            >
              Generate Quotation
            </button>
          )}
      </div>
    </Fragment>
  );
}
